import { faAppleWhole, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useContext } from 'react';

import { VERIFICATION_FIELDS } from '@/components/InsuranceSection/constants';
import { StringKeySignature } from '@/components/InsuranceSection/types';
import { Field, FieldTypes } from '@/components/PersonInPlaceEditor/types';
import { serviceCodeDropdownOptions } from '@/components/PersonInsurancePolicy/NewVOB/serviceCodeOptions';
import { PictureUploadKind } from '@/constants/s3DirectFileUpload';
import AppContext from '@/contexts/appContext';
import useConstants from '@/hooks/useConstants';
import useCountries from '@/hooks/useCountries';
import useInsurancePlans from '@/hooks/useInsurancePlans';
import useInsurancePlanTypes from '@/hooks/useInsurancePlanTypes';
import useInsurancePriorityTypes from '@/hooks/useInsurancePriorityTypes';
import useInsuranceProviders from '@/hooks/useInsuranceProviders';
import useInsuranceRelationships from '@/hooks/useInsuranceRelationships';
import useInsuranceVerificationSources from '@/hooks/useInsuranceVerificationSources';
import useInsuranceVerificationStatuses from '@/hooks/useInsuranceVerificationStatuses';
import useUploadImage from '@/hooks/useUploadImage';
import PersonContext from '@/pages/Person/PersonContext';
import {
  InsuranceCardImages,
  InsurancePolicy as InsurancePolicyInterface
} from '@/types/insurancePolicy';
import Tooltip from '@/ui/components/Tooltip';

const PRIORITY_TYPES_TO_FILTER = ['Inactive'];

const ImageTooltip = ({ url }: { url: string }) => (
  <Tooltip tooltipContent={<img loading="lazy" src={url} alt="Front" className="w-50" />}>
    <a className="text-blue-800" target="_blank" rel="noreferrer" href={url}>
      <FontAwesomeIcon icon={faMagnifyingGlass} className="mr-1" />
      Current Image
    </a>
  </Tooltip>
);

const RequiredForHealthieIcon = () => (
  <Tooltip tooltipContent="Required for Healthie sync">
    <FontAwesomeIcon icon={faAppleWhole} className="text-gray-500 ml-2" />
  </Tooltip>
);

const useInsurancePolicyFormFields = ({
  includeVerificationFields
}: {
  includeVerificationFields: boolean;
}) => {
  const { addMessage, host } = useContext(AppContext);
  const {
    person: { id: personId }
  } = useContext(PersonContext);

  const { getUploadConfig, uploadFile } = useUploadImage({ addMessage, host });

  const { refetch: getInsuranceProviders } = useInsuranceProviders(false);
  const formatInsuranceProviders = useCallback(async () => {
    const { data: insuranceProviders } = await getInsuranceProviders();
    if (insuranceProviders?.data)
      return insuranceProviders.data.map(({ internal_name }) => ({
        label: internal_name,
        value: internal_name
      }));
    return [];
  }, [getInsuranceProviders]);

  const { refetch: getInsurancePlans } = useInsurancePlans(false);
  const { refetch: getInsurancePlanTypes } = useInsurancePlanTypes(false);
  const formatInsurancePlans = useCallback(async () => {
    const { data: insurancePlans } = await getInsurancePlans();
    if (insurancePlans?.data)
      return insurancePlans.data.map(({ name_and_id, id }) => ({
        label: name_and_id,
        value: id
      }));
    return [];
  }, [getInsurancePlans]);

  const { refetch: getConstants } = useConstants(false);
  const formatInsuranceTypes = useCallback(async () => {
    const { data: insurancePlanTypes } = await getInsurancePlanTypes();
    if (insurancePlanTypes?.data)
      return insurancePlanTypes.data.map(({ label, value }) => ({
        label,
        value
      }));
    return [];
  }, [getInsurancePlanTypes]);
  const formatStates = useCallback(async () => {
    const { data: usStates } = await getConstants();
    if (usStates?.data)
      return usStates.data.us_states.map(({ name }) => ({ label: name, value: name }));
    return [];
  }, [getConstants]);

  const { refetch: getInsurancePriorityTypes } = useInsurancePriorityTypes(false);
  const formatInsurancePriorityTypes = useCallback(async () => {
    const { data: priorityTypes } = await getInsurancePriorityTypes();
    if (priorityTypes?.data)
      return priorityTypes.data
        .filter((priorityType) => !PRIORITY_TYPES_TO_FILTER.includes(priorityType))
        .map((priorityType) => ({
          label: priorityType,
          value: priorityType
        }));
    return [];
  }, [getInsurancePriorityTypes]);

  const { refetch: getInsuranceRelationships } = useInsuranceRelationships(false);
  const formatInsuranceRelationships = useCallback(async () => {
    const { data: relationships } = await getInsuranceRelationships();
    if (relationships?.data)
      return relationships.data.map((relationship) => ({
        label: relationship,
        value: relationship
      }));
    return [];
  }, [getInsuranceRelationships]);

  const countriesForSelect = useCountries().map((country) => ({
    label: country.name,
    value: country.code
  }));

  const { refetch: getInsuranceVerificationSources } = useInsuranceVerificationSources(false);
  const formatInsuranceVerificationSources = useCallback(async () => {
    const { data: insuranceVerificationSources } = await getInsuranceVerificationSources();
    if (insuranceVerificationSources)
      return insuranceVerificationSources.map((insuranceVerificationSource) => ({
        label: insuranceVerificationSource.name,
        value: insuranceVerificationSource.id
      }));
    return [];
  }, [getInsuranceVerificationSources]);

  const { refetch: getInsuranceVerificationStatuses } = useInsuranceVerificationStatuses(false);
  const formatInsuranceVerificationStatuses = useCallback(async () => {
    const { data: insuranceVerificationStatuses } = await getInsuranceVerificationStatuses();
    if (insuranceVerificationStatuses)
      return insuranceVerificationStatuses.map((insuranceVerificationStatus) => ({
        label: insuranceVerificationStatus,
        value: insuranceVerificationStatus
      }));
    return [];
  }, [getInsuranceVerificationStatuses]);

  const verificationFields = () => [
    {
      key: VERIFICATION_FIELDS.new_verification_toggle,
      label: 'Add New Verification',
      type: FieldTypes.BOOLEAN
    },
    {
      key: VERIFICATION_FIELDS.status,
      control: VERIFICATION_FIELDS.new_verification_toggle,
      label: 'Verification Status',
      type: FieldTypes.SELECT,
      options: formatInsuranceVerificationStatuses
    },
    {
      key: VERIFICATION_FIELDS.service_code,
      control: VERIFICATION_FIELDS.new_verification_toggle,
      label: 'Service Code',
      type: FieldTypes.SELECT,
      options: serviceCodeDropdownOptions
    },
    {
      key: VERIFICATION_FIELDS.verified_through,
      control: VERIFICATION_FIELDS.new_verification_toggle,
      label: 'Verified Through',
      type: FieldTypes.DATE
    },
    {
      key: VERIFICATION_FIELDS.verified_via,
      control: VERIFICATION_FIELDS.new_verification_toggle,
      label: 'Verification Source',
      options: formatInsuranceVerificationSources,
      type: FieldTypes.SELECT
    },
    {
      key: VERIFICATION_FIELDS.estimated_initial_month_cost,
      control: VERIFICATION_FIELDS.new_verification_toggle,
      label: 'Estimated Initial Month Cost (in dollars)',
      type: FieldTypes.CURRENCY
    },
    {
      key: VERIFICATION_FIELDS.estimated_monthly_cost,
      control: VERIFICATION_FIELDS.new_verification_toggle,
      label: 'Estimated Monthly Cost (in dollars)',
      type: FieldTypes.CURRENCY
    },
    {
      key: VERIFICATION_FIELDS.deductible_remaining,
      control: VERIFICATION_FIELDS.new_verification_toggle,
      label: 'Deductible Remaining',
      type: FieldTypes.CURRENCY
    },
    {
      key: VERIFICATION_FIELDS.vob_notes,
      control: VERIFICATION_FIELDS.new_verification_toggle,
      label: 'VOB Notes',
      type: FieldTypes.RICH_TEXT
    }
  ];

  const buildFields = ({
    personIdField,
    insuranceCardImages
  }: {
    personIdField: boolean;
    insuranceCardImages?: InsuranceCardImages;
  }) => [
    {
      key: 'id',
      label: '',
      type: FieldTypes.HIDDEN
    },
    {
      alwaysSend: personIdField,
      key: 'person_id',
      label: '',
      type: FieldTypes.HIDDEN
    },
    {
      key: 'is_mutable',
      label: '',
      type: FieldTypes.HIDDEN
    },
    {
      additionalCaption: {
        label: '',
        value: insuranceCardImages?.front_image_url ? (
          <ImageTooltip url={insuranceCardImages.front_image_url} />
        ) : (
          'No current image'
        )
      },
      key: 'front_image_url',
      label: 'Upload Insurance Card Front',
      type: FieldTypes.FILE
    },
    {
      additionalCaption: {
        label: '',
        value: insuranceCardImages?.back_image_url ? (
          <ImageTooltip url={insuranceCardImages.back_image_url} />
        ) : (
          'No current image'
        )
      },
      key: 'back_image_url',
      label: 'Upload Insurance Card Back',
      type: FieldTypes.FILE
    },
    {
      key: 'carrier',
      label: (
        <>
          Carrier
          <RequiredForHealthieIcon />
        </>
      ),
      type: FieldTypes.SELECT,
      options: formatInsuranceProviders,
      placeholder: 'Select Carrier'
    },
    {
      key: 'plan_healthie_id',
      label: (
        <>
          Plan
          <RequiredForHealthieIcon />
        </>
      ),
      type: FieldTypes.SELECT,
      options: formatInsurancePlans,
      placeholder: 'Select Plan'
    },
    {
      key: 'subscriber_id',
      label: (
        <>
          Subscriber ID
          <RequiredForHealthieIcon />
        </>
      ),
      type: FieldTypes.TEXT,
      placeholder: 'Enter Subscriber ID'
    },
    {
      key: 'policy_member_id',
      label: 'Member ID',
      type: FieldTypes.TEXT,
      placeholder: 'Enter Member ID',
      additionalCaption: {
        value: 'The member ID if different from the policy holder ID.'
      }
    },
    {
      key: 'type',
      label: (
        <>
          Type
          <RequiredForHealthieIcon />
        </>
      ),
      type: FieldTypes.SELECT,
      options: formatInsuranceTypes,
      placeholder: 'Select Type'
    },
    {
      key: 'priority_type',
      label: (
        <>
          Order
          <RequiredForHealthieIcon />
        </>
      ),
      type: FieldTypes.RADIO,
      options: formatInsurancePriorityTypes,
      placeholder: 'Select Order'
    },
    {
      key: 'relationship',
      label: (
        <>
          Relationship
          <RequiredForHealthieIcon />
        </>
      ),
      type: FieldTypes.SELECT,
      options: formatInsuranceRelationships,
      placeholder: 'Select Relationship'
    },
    {
      key: 'plan_start_date',
      label: 'Plan Start Date',
      type: FieldTypes.DATE
    },
    {
      key: 'plan_end_date',
      label: 'Plan End Date',
      type: FieldTypes.DATE
    },
    {
      key: 'policyholder_legal_name',
      label: 'Policyholder Name',
      type: FieldTypes.TEXT,
      readOnly: true
    },
    {
      key: 'policyholder_first_name',
      label: 'Policyholder First Name',
      type: FieldTypes.TEXT
    },
    {
      key: 'policyholder_last_name',
      label: 'Policyholder Last Name',
      type: FieldTypes.TEXT
    },
    {
      key: 'policyholder_dob',
      label: 'Policyholder DOB',
      type: FieldTypes.DATE
    },
    ...(includeVerificationFields ? verificationFields() : []),
    {
      key: 'annual_deductible',
      hidden: true,
      label: 'Annual Deductible (dollars)',
      type: FieldTypes.CURRENCY
    },
    {
      key: 'co_insurance',
      hidden: true,
      label: 'Co-insurance (percentage)',
      type: FieldTypes.PERCENTAGE
    },
    {
      key: 'co_pay',
      hidden: true,
      label: 'Co-pay (dollars)',
      type: FieldTypes.CURRENCY
    },
    {
      key: 'co_pay_unit',
      hidden: true,
      label: 'Co-pay unit',
      type: FieldTypes.TEXT
    },
    {
      key: 'out_of_pocket_maximum',
      hidden: true,
      label: 'Out of Pocket Maximum (dollars)',
      type: FieldTypes.CURRENCY
    },
    {
      key: 'insurer_address_line_1',
      hidden: true,
      label: 'Insurer Address',
      type: FieldTypes.TEXT
    },
    {
      key: 'insurer_address_line_2',
      hidden: true,
      label: 'Apt, suite, unit, building, floor, etc',
      type: FieldTypes.TEXT
    },
    {
      key: 'insurer_city',
      hidden: true,
      label: 'City',
      type: FieldTypes.TEXT
    },
    {
      key: 'insurer_state',
      hidden: true,
      label: 'State',
      type: FieldTypes.SELECT,
      options: formatStates
    },
    {
      key: 'insurer_country',
      hidden: true,
      label: 'Country',
      type: FieldTypes.SELECT,
      options: countriesForSelect
    },
    {
      key: 'insurer_zip_code',
      hidden: true,
      label: 'Zip Code',
      type: FieldTypes.TEXT
    },
    {
      key: 'group_id',
      hidden: true,
      label: 'Group ID',
      type: FieldTypes.TEXT
    },
    {
      key: 'health_plan_id',
      hidden: true,
      label: 'Health Plan ID',
      type: FieldTypes.TEXT
    },
    {
      key: 'provider_phone_number',
      hidden: true,
      label: 'Insurance Phone Number',
      type: FieldTypes.TEXT
    },
    {
      key: 'rx_bin',
      hidden: true,
      label: 'RX Bin',
      type: FieldTypes.TEXT
    },
    {
      key: 'rx_group',
      hidden: true,
      label: 'RX Group',
      type: FieldTypes.TEXT
    },
    {
      key: 'guarantor',
      hidden: true,
      label: 'Guarantor',
      type: FieldTypes.TEXT
    }
  ];

  const submittedValuesManipulator = async (values: Partial<InsurancePolicyInterface>) => {
    const pictureUploadKindMap: { [key: string]: PictureUploadKind } = {
      front_image_url: PictureUploadKind.insuranceCardFrontImage,
      back_image_url: PictureUploadKind.insuranceCardBackImage
    };
    const insuranceCardKeys = ['front_image_url', 'back_image_url'];
    if (insuranceCardKeys.some((key) => Object.keys(values).includes(key))) {
      for (const key of insuranceCardKeys) {
        if (Object.keys(values).includes(key)) {
          const value = (values as StringKeySignature)[key];
          if (value instanceof File) {
            delete (values as StringKeySignature)[key];

            try {
              const config = await getUploadConfig(
                {
                  contentType: value.type,
                  pictureKind: pictureUploadKindMap[key] as PictureUploadKind
                },
                personId
              );

              if (!config) {
                addMessage('error', `Failed to upload insurance card (${key}).`);
                continue;
              }

              const uploadStatus = await uploadFile(value, config);

              // uploadToS3 returns a status of 204 if successful
              if (
                uploadStatus?.status &&
                200 <= uploadStatus.status &&
                uploadStatus.status <= 299
              ) {
                const uploadedFileName = config.fields.key;
                if (values['insurance_card_images']) {
                  (values['insurance_card_images'] as StringKeySignature)[key] = uploadedFileName;
                } else {
                  values['insurance_card_images'] = {
                    [key]: uploadedFileName
                  };
                }
              } else {
                addMessage('error', `Failed to upload insurance card (${key}).`);
              }
            } catch (error) {
              addMessage('error', `Failed to upload insurance card (${key}). ${error}`);
            }
          }
        }
      }
    }

    return values;
  };

  return {
    buildFields: buildFields as (options: {
      personIdField: boolean;
      insuranceCardImages?: InsuranceCardImages;
    }) => Field<InsurancePolicyInterface>[],
    submittedValuesManipulator
  };
};

export default useInsurancePolicyFormFields;
